import React, { useEffect } from 'react';
import { ArtworkColor, GalleryItem } from '../../types/gallery';
import './ColorFilterPill.css';

export interface ColorFilterOption {
  value: ArtworkColor;
  label: string;
  tooltip: string;
  paintClass: string;
  imageSrc: string;
}

// Order of the rainbow -> rainbow -> gray (noColor)
export const COLOR_FILTER_OPTIONS: ColorFilterOption[] = [
  { value: 'red', label: 'Red', tooltip: 'Filter by Red', paintClass: 'paint-red', imageSrc: 'https://ik.imagekit.io/schzstrg/tr:w-200,h-200,q-75/red.png' },
  { value: 'orange', label: 'Orange', tooltip: 'Filter by Orange', paintClass: 'paint-orange', imageSrc: 'https://ik.imagekit.io/schzstrg/tr:w-200,h-200,q-75/orange.png' },
  { value: 'yellow', label: 'Yellow', tooltip: 'Filter by Yellow', paintClass: 'paint-yellow', imageSrc: 'https://ik.imagekit.io/schzstrg/tr:w-200,h-200,q-75/yellow.png' },
  { value: 'green', label: 'Green', tooltip: 'Filter by Green', paintClass: 'paint-green', imageSrc: 'https://ik.imagekit.io/schzstrg/tr:w-200,h-200,q-75/green.png' },
  { value: 'blue', label: 'Blue', tooltip: 'Filter by Blue', paintClass: 'paint-blue', imageSrc: 'https://ik.imagekit.io/schzstrg/tr:w-200,h-200,q-75/blue.png' },
  { value: 'purple', label: 'Purple', tooltip: 'Filter by Purple', paintClass: 'paint-purple', imageSrc: 'https://ik.imagekit.io/schzstrg/tr:w-200,h-200,q-75/purple.png' },
  { value: 'pink', label: 'Pink', tooltip: 'Filter by Pink', paintClass: 'paint-pink', imageSrc: 'https://ik.imagekit.io/schzstrg/tr:w-200,h-200,q-75/pink.png' },
  { value: 'rainbow', label: 'Rainbow', tooltip: 'Filter by Rainbow / Multicolored', paintClass: 'paint-rainbow', imageSrc: 'https://ik.imagekit.io/schzstrg/tr:w-200,h-200,q-75/rainbow.png' },
  { value: 'noColor', label: 'Monochrome', tooltip: 'Filter by Monochrome', paintClass: 'paint-noColor', imageSrc: 'https://ik.imagekit.io/schzstrg/tr:w-200,h-200,q-75/noColor.png' },
];

export interface ColorFilterPillProps {
  activeColor: ArtworkColor | null;
  onSelectColor: (color: ArtworkColor | null) => void;
  items?: GalleryItem[];
  className?: string;
}

export const ColorFilterPill: React.FC<ColorFilterPillProps> = ({
  activeColor,
  onSelectColor,
  className = '',
}) => {
  // Preload canvas texture and all 9 watercolor PNG assets on mount for immediate response
  useEffect(() => {
    const canvasImg = new Image();
    canvasImg.src = 'https://ik.imagekit.io/schzstrg/tr:w-500,h-500,q-75/canvas.png';

    COLOR_FILTER_OPTIONS.forEach((opt) => {
      const img = new Image();
      img.src = opt.imageSrc;
    });
  }, []);

  // Toggle color filter: pressing the active filter again dismisses it
  const handleButtonClick = (color: ArtworkColor) => {
    if (typeof window !== 'undefined' && (window as any).__SCHZ_COLLAPSED__) return;
    if (activeColor === color) {
      onSelectColor(null);
    } else {
      onSelectColor(color);
    }
  };

  return (
    <nav
      className={`color-filter-nav ${className}`}
      aria-label="Filter gallery artworks by color"
      style={{ zIndex: 50, overflow: 'visible' }}
    >
      <div className="color-filter-pill-wrapper">
        <div className="color-filter-track canvas-board" role="toolbar">
          {/* Authentic Square Canvas Texture (canvas.png rotated 90 degrees and set to fit) */}
          <div
            className="canvas-texture-layer"
            style={{ backgroundImage: "url('https://ik.imagekit.io/schzstrg/tr:w-500,h-500,q-75/canvas.png')" }}
            aria-hidden="true"
          />

          {/* 9 Watercolor Blobs on Square Canvas Board */}
          {COLOR_FILTER_OPTIONS.map((opt) => {
            const isSelected = activeColor === opt.value;

            return (
              <button
                key={opt.value}
                type="button"
                className={`color-filter-btn ${opt.paintClass} ${isSelected ? 'active' : ''}`}
                onClick={() => handleButtonClick(opt.value)}
                aria-pressed={isSelected}
                aria-label={`Filter by ${opt.label}${isSelected ? ' (Active) - click to dismiss' : ''}`}
                title={`${opt.label}${isSelected ? ' — Click to dismiss filter' : ''}`}
              >
                {/* Rainbow Authentic Multi-Color Blurred Glow Layers */}
                {opt.value === 'rainbow' && (
                  <>
                    <img
                      src={opt.imageSrc}
                      alt=""
                      className="watercolor-rainbow-glow watercolor-rainbow-glow-spread"
                      draggable={false}
                      aria-hidden="true"
                    />
                    <img
                      src={opt.imageSrc}
                      alt=""
                      className="watercolor-rainbow-glow watercolor-rainbow-glow-core"
                      draggable={false}
                      aria-hidden="true"
                    />
                  </>
                )}
                <img
                  src={opt.imageSrc}
                  alt=""
                  className={`watercolor-blob watercolor-${opt.value}`}
                  draggable={false}
                  loading="eager"
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default ColorFilterPill;
